// Memoized to avoid recomputing descriptors on every digest cycle.
const descriptorCache = new WeakMap();

/**
 * Given a display font and the text-sample map, return descriptors in the
 * shape expected by the `<af-display-in-font>` web component. When
 * `extraText` is provided, its codepoints are appended to `unicodeRange` so
 * the initial subset request covers a persisted custom sample text without an extra augment call.
 *
 * @param {Object} displayFont
 * @param {Object} textSamples Map keyed by IANA language tag
 * @param {string} [extraText] Additional text whose codepoints should be included
 * @returns {Object}
 */
function getDisplayInFontDescriptors(displayFont, textSamples, extraText) {
  let base = descriptorCache.get(displayFont);
  if (!base) {
    const fvd = displayFont.font.web.fvd;
    const unicodeRange = displayFont.preview_text?.unicode_range
      || textSamples[displayFont.default_language]?.unicode_range;

    base = {
      webId: displayFont.family.web_id,
      fontStyle: fvd[0] === 'n' ? 'normal' : (fvd[0] === 'i' ? 'italic' : 'oblique'),
      fontWeight: Number(fvd[1]) * 100,
      fontFeatureSettings: displayFont.font.web.sample_text_features || '',
      unicodeRange: unicodeRange || ''
    };
    descriptorCache.set(displayFont, base);
  }
  if (!extraText) {
    return base;
  }
  return { ...base, unicodeRange: extendUnicodeRange(base.unicodeRange, extraText) };
}

function extendUnicodeRange(baseRange, text) {
  if (!text) {
    return baseRange;
  }
  const codepoints = Array.from(text, c => 'U+' + c.codePointAt(0).toString(16)).join(',');
  return baseRange ? `${baseRange},${codepoints}` : codepoints;
}

module.exports = { getDisplayInFontDescriptors, extendUnicodeRange };
